import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {
  pinIssueMutation as pinIssueQueryId,
  unpinIssueMutation as unpinIssueQueryId,
} from '@github-ui/server-persisted-queries'
import type {PinIssueMutationMutation} from '@github-ui/server-persisted-queries/types/pinIssueMutation'
import type {UnpinIssueMutationMutation} from '@github-ui/server-persisted-queries/types/unpinIssueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'react-relay/hooks'
import type {Disposable, Environment, RecordSourceProxy} from 'relay-runtime'

import {ERRORS} from '../constants/errors'
import {issueLifecyclePath} from './issue-lifecycle-mutation'

type PinIssuePayload = NonNullable<PinIssueMutationMutation['pinIssue']>
type UnpinIssuePayload = NonNullable<UnpinIssueMutationMutation['unpinIssue']>
type PinningIssue = NonNullable<PinIssuePayload['issue'] | UnpinIssuePayload['issue']>

type IssuePinningContext = {
  relayUpdate: Disposable
}

type GraphQLResponse<TData> = {
  data?: TData | null
  errors?: ReadonlyArray<{message?: string}> | null
}

type IssueLifecyclePathParameters = Parameters<typeof issueLifecyclePath>

async function readGraphQLResponse<TData>(response: Response, errorMessage: string): Promise<TData> {
  if (!response.ok) {
    throw new Error(`${errorMessage} (${response.status})`)
  }

  const json = (await response.json()) as GraphQLResponse<TData>
  if (!json.data) throw new Error(json.errors?.[0]?.message ?? errorMessage)

  return json.data
}

async function submitIssuePinningMutation({
  pinned,
  issueId,
  repositoryId,
  owner,
  repository,
  issueNumber,
}: {
  pinned: boolean
  issueId: PinningIssue['id']
  repositoryId: PinningIssue['repository']['id']
  owner: IssueLifecyclePathParameters[0]
  repository: IssueLifecyclePathParameters[1]
  issueNumber: IssueLifecyclePathParameters[2]
}) {
  const fallbackMessage = pinned ? ERRORS.couldNotPinIssue : ERRORS.couldNotUnpinIssue
  const queryId = pinned ? pinIssueQueryId : unpinIssueQueryId
  const method = pinned ? 'PUT' : 'DELETE'
  const path = `${issueLifecyclePath(owner, repository, issueNumber)}/pin`

  const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId})}`, {method})

  let issue: PinningIssue | null | undefined
  let unpinnedRecordId: NonNullable<UnpinIssuePayload['id']> | null = null
  if (pinned) {
    const data = await readGraphQLResponse<PinIssueMutationMutation>(response, fallbackMessage)
    if (!data.pinIssue) throw new Error(fallbackMessage)
    issue = data.pinIssue.issue
  } else {
    const data = await readGraphQLResponse<UnpinIssueMutationMutation>(response, fallbackMessage)
    if (!data.unpinIssue || data.unpinIssue.id == null) throw new Error(fallbackMessage)
    issue = data.unpinIssue.issue
    unpinnedRecordId = data.unpinIssue.id
  }

  const pinnedIssues = issue?.repository.pinnedIssues
  if (issue?.id !== issueId || issue.isPinned == null || issue.repository.id !== repositoryId || !pinnedIssues) {
    throw new Error(fallbackMessage)
  }

  return {
    issue: {
      ...issue,
      isPinned: issue.isPinned,
      repository: {...issue.repository, pinnedIssues},
    },
    unpinnedRecordId,
  }
}

type IssuePinningResult = Awaited<ReturnType<typeof submitIssuePinningMutation>>

type IssuePinningOptions = Omit<Parameters<typeof submitIssuePinningMutation>[0], 'pinned'> & {
  currentPinnedCount: IssuePinningResult['issue']['repository']['pinnedIssues']['totalCount']
  onError?: (error: Error) => void
  onCompleted?: () => void
}

function updateRelayPinningState(
  store: RecordSourceProxy,
  issueId: string,
  repositoryId: string,
  isPinned: boolean,
  pinnedIssuesCount: number,
) {
  store.get(issueId)?.setValue(isPinned, 'isPinned')
  store.get(repositoryId)?.getLinkedRecord('pinnedIssues', {first: 3})?.setValue(pinnedIssuesCount, 'totalCount')
}

// RELAY-CLEANUP(use-issue-pinning): mirror native pin/unpin into the primary Relay query until its
// canonical issue and repository fields migrate to TanStack.
function applyOptimisticRelayUpdate(
  environment: Environment,
  issueId: string,
  repositoryId: string,
  isPinned: boolean,
  pinnedIssuesCount: number,
): Disposable {
  return environment.applyUpdate({
    storeUpdater: store => updateRelayPinningState(store, issueId, repositoryId, isPinned, pinnedIssuesCount),
  })
}

function settleRelayUpdate(environment: Environment, result: IssuePinningResult, relayUpdate: Disposable) {
  commitLocalUpdate(environment, store => {
    updateRelayPinningState(
      store,
      result.issue.id,
      result.issue.repository.id,
      result.issue.isPinned,
      result.issue.repository.pinnedIssues.totalCount,
    )
    if (result.unpinnedRecordId) store.delete(result.unpinnedRecordId)
  })
  relayUpdate.dispose()
}

function useIssuePinningMutation(pinned: boolean, options: IssuePinningOptions) {
  const {issueId, repositoryId, owner, repository, issueNumber, currentPinnedCount, onError, onCompleted} = options
  const environment = useRelayEnvironment()
  const optimisticCount = pinned ? currentPinnedCount + 1 : Math.max(0, currentPinnedCount - 1)

  const {mutate} = useEntityMutation<IssuePinningResult, Error, IssuePinningContext>({
    scope: {id: `issue-pinning:${issueId}`},
    mutationFn: () => submitIssuePinningMutation({pinned, issueId, repositoryId, owner, repository, issueNumber}),
    getConfirmedOperation: result => ({
      type: 'setFields',
      id: result.issue.id,
      fields: {
        isPinned: result.issue.isPinned,
        repository: {
          id: result.issue.repository.id,
          pinnedIssues: {totalCount: result.issue.repository.pinnedIssues.totalCount},
        },
      },
    }),
    onMutate: () => ({
      relayUpdate: applyOptimisticRelayUpdate(environment, issueId, repositoryId, pinned, optimisticCount),
    }),
    onError: (error, _variables, context) => {
      context?.relayUpdate.dispose()
      onError?.(error)
    },
    onSuccess: (result, _variables, context) => {
      if (context) settleRelayUpdate(environment, result, context.relayUpdate)
      onCompleted?.()
    },
  })

  return useCallback(() => {
    const operation: SetFieldsOperation = {
      type: 'setFields',
      id: issueId,
      fields: {
        isPinned: pinned,
        repository: {
          id: repositoryId,
          pinnedIssues: {totalCount: optimisticCount},
        },
      },
    }
    mutate({operation})
  }, [issueId, mutate, optimisticCount, pinned, repositoryId])
}

export function usePinIssue(options: IssuePinningOptions) {
  return useIssuePinningMutation(true, options)
}

export function useUnpinIssue(options: IssuePinningOptions) {
  return useIssuePinningMutation(false, options)
}
